import { Link } from "react-router-dom";
import { ArrowRight, Clock, TrendingDown, Zap, Smile } from "lucide-react";

import { useTranslation } from 'react-i18next';

const metricIcons = {
  hoursSaved: Clock,
  costReduction: TrendingDown,
  responseTimeImprovement: Zap,
  satisfaction: Smile,
};

export default function CaseStudyCard({ caseStudy }) {
  const { t } = useTranslation();
  if (!caseStudy) return null;

  const metrics = caseStudy.results || [];
  const tags = caseStudy.tags || [];

  return (
    <Link
      to={`/cases/${caseStudy.slug}`}
      className="group relative flex flex-col bg-[#09090B] border border-zinc-900 hover:border-amber-500/50 transition-all duration-300 hover:shadow-[0_0_30px_-10px_rgba(245,158,11,0.15)] hover:-translate-y-1"
      role="listitem"
    >
      {/* Top accent line */}
      <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-amber-500/0 to-transparent group-hover:via-amber-500 transition-all duration-500" />

      {/* Industry tag + category */}
      <div className="px-6 pt-6 pb-3 flex items-center justify-between">
        <span className="inline-block font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 border border-amber-500/30 px-2 py-1">
          {caseStudy.industryTag}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-600">
          {caseStudy.employees}
        </span>
      </div>

      {/* Content */}
      <div className="px-6 pb-6 flex flex-col flex-grow">
        <h3 className="font-display text-xl uppercase leading-tight text-white group-hover:text-amber-500 transition-colors duration-300 mb-1">
          {caseStudy.company}
        </h3>

        <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500 mb-4">
          {caseStudy.industry}
        </p>

        {/* Metric headline */}
        <p className="text-2xl font-display text-amber-500 uppercase leading-tight mb-3">
          {caseStudy.headline}
        </p>

        <p className="text-zinc-400 text-sm leading-relaxed line-clamp-3 mb-5 flex-grow">
          {caseStudy.excerpt}
        </p>

        {/* Visual metrics row */}
        <div className="grid grid-cols-2 gap-2 mb-5">
          {metrics.slice(0, 4).map((m, i) => (
            <div
              key={i}
              className="bg-zinc-900/50 border border-zinc-800 px-3 py-2 group-hover:border-zinc-700 transition-colors duration-300"
            >
              <div className="font-display text-lg text-amber-500 leading-none mb-0.5">
                {m.value}
              </div>
              <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-500">
                {m.label}
              </div>
            </div>
          ))}
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {tags.map((tag) => (
              <span
                key={tag}
                className="font-mono text-[9px] uppercase tracking-[0.1em] text-zinc-500 border border-zinc-800 px-1.5 py-0.5 group-hover:border-zinc-700 group-hover:text-zinc-400 transition-colors duration-300"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* CTA */}
        <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 group-hover:gap-3 transition-all duration-300 mt-auto">
          {t('case_studies_section.read_full_case')} <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </Link>
  );
}
